import { z } from "zod";

import type { CardCatalog } from "./catalog-validation.js";
import { CardTypeSchema } from "./card-schema.js";
import {
  CardDefinitionIdSchema,
  DefinitionVersionSchema,
  PresentationAssetIdSchema,
  SCHEMA_VERSION,
  SemanticVersionSchema,
} from "./primitives.js";
import { FATE_KEYWORD_IDS } from "./keyword-registry.js";

export const PRESENTATION_ASSET_TYPES = [
  "art-layer",
  "material-profile",
  "audio-cue",
  "cinematic",
] as const;

export const PresentationAssetTypeSchema = z.enum(PRESENTATION_ASSET_TYPES);

export const PresentationAssetSchema = z
  .object({
    id: PresentationAssetIdSchema,
    type: PresentationAssetTypeSchema,
    source: z.string().trim().min(1).max(500),
  })
  .strict();

export const PresentationCardMetadataSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    definitionVersion: DefinitionVersionSchema,
    cardId: CardDefinitionIdSchema,
    artLayerIds: z.array(PresentationAssetIdSchema),
    focus: z
      .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
      })
      .strict()
      .optional(),
    materialProfileId: PresentationAssetIdSchema.optional(),
    audioCueIds: z.array(PresentationAssetIdSchema),
    cinematicId: PresentationAssetIdSchema.optional(),
  })
  .strict();

export const StructuredCardPresentationSchema = z
  .object({
    schemaVersion: z.literal(2),
    cardId: CardDefinitionIdSchema,
    displayName: z.string().trim().min(1).max(120),
    cardType: CardTypeSchema,
    faction: z.string().trim().min(1).max(80),
    rarity: z.enum(["common", "uncommon", "rare", "epic", "legendary", "test-only"]),
    fullArtAssetId: PresentationAssetIdSchema.nullable(),
    croppedArtAssetId: PresentationAssetIdSchema.nullable(),
    artAlt: z.string().trim().min(1).max(500),
    summary: z.string().trim().min(1).max(320),
    fullEffectText: z.string().trim().min(1).max(8000),
    ruleReferences: z.array(z.string().trim().min(1).max(160)),
    keywordIds: z.array(z.string().trim().min(1).max(80)),
    lore: z.string().trim().max(4000),
    flavorText: z.string().trim().max(1000),
    artistCredit: z.string().trim().min(1).max(160),
    setId: z.string().trim().min(1).max(120),
  })
  .strict()
  .superRefine((card, context) => {
    card.keywordIds.forEach((keywordId, index) => {
      if (!FATE_KEYWORD_IDS.has(keywordId)) {
        context.addIssue({
          code: "custom",
          path: ["keywordIds", index],
          message: `references unknown Fate Kingdom keyword '${keywordId}'`,
        });
      }
    });
  });

function addDuplicateIssue(
  values: readonly string[],
  path: string,
  context: z.RefinementCtx,
): void {
  const firstIndexByValue = new Map<string, number>();
  values.forEach((value, index) => {
    const firstIndex = firstIndexByValue.get(value);
    if (firstIndex === undefined) {
      firstIndexByValue.set(value, index);
      return;
    }

    context.addIssue({
      code: "custom",
      path: [path, index, path === "assets" ? "id" : "cardId"],
      message: `duplicates ${path}[${firstIndex}] reference '${value}'`,
    });
  });
}

function addAssetReferenceIssue(
  assetId: string,
  expectedType: z.infer<typeof PresentationAssetTypeSchema>,
  path: readonly (string | number)[],
  assetTypes: ReadonlyMap<string, z.infer<typeof PresentationAssetTypeSchema>>,
  context: z.RefinementCtx,
): void {
  const actualType = assetTypes.get(assetId);
  if (actualType === undefined) {
    context.addIssue({
      code: "custom",
      path: [...path],
      message: `references unknown presentation asset '${assetId}'`,
    });
    return;
  }

  if (actualType !== expectedType) {
    context.addIssue({
      code: "custom",
      path: [...path],
      message: `requires a '${expectedType}' asset but '${assetId}' is '${actualType}'`,
    });
  }
}

export const PresentationCatalogSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    cardDataVersion: SemanticVersionSchema,
    assets: z.array(PresentationAssetSchema),
    cards: z.array(PresentationCardMetadataSchema),
  })
  .strict()
  .superRefine((catalog, context) => {
    addDuplicateIssue(
      catalog.assets.map((asset) => asset.id),
      "assets",
      context,
    );
    addDuplicateIssue(
      catalog.cards.map((card) => card.cardId),
      "cards",
      context,
    );

    const assetTypes = new Map(catalog.assets.map((asset) => [asset.id, asset.type]));
    catalog.cards.forEach((card, cardIndex) => {
      card.artLayerIds.forEach((assetId, assetIndex) => {
        addAssetReferenceIssue(
          assetId,
          "art-layer",
          ["cards", cardIndex, "artLayerIds", assetIndex],
          assetTypes,
          context,
        );
      });
      if (card.materialProfileId !== undefined) {
        addAssetReferenceIssue(
          card.materialProfileId,
          "material-profile",
          ["cards", cardIndex, "materialProfileId"],
          assetTypes,
          context,
        );
      }
      card.audioCueIds.forEach((assetId, assetIndex) => {
        addAssetReferenceIssue(
          assetId,
          "audio-cue",
          ["cards", cardIndex, "audioCueIds", assetIndex],
          assetTypes,
          context,
        );
      });
      if (card.cinematicId !== undefined) {
        addAssetReferenceIssue(
          card.cinematicId,
          "cinematic",
          ["cards", cardIndex, "cinematicId"],
          assetTypes,
          context,
        );
      }
    });
  });

export function validatePresentationCatalog(input: unknown, gameplayCatalog: CardCatalog) {
  return PresentationCatalogSchema.superRefine((presentation, context) => {
    if (presentation.cardDataVersion !== gameplayCatalog.cardDataVersion) {
      context.addIssue({
        code: "custom",
        path: ["cardDataVersion"],
        message: `must match gameplay catalog version '${gameplayCatalog.cardDataVersion}'`,
      });
    }

    const gameplayCardIds = new Set(gameplayCatalog.cards.map((card) => card.id));
    presentation.cards.forEach((card, cardIndex) => {
      if (!gameplayCardIds.has(card.cardId)) {
        context.addIssue({
          code: "custom",
          path: ["cards", cardIndex, "cardId"],
          message: `references unknown gameplay card '${card.cardId}'`,
        });
      }
    });
  }).safeParse(input);
}

export type PresentationAsset = z.infer<typeof PresentationAssetSchema>;
export type PresentationAssetType = z.infer<typeof PresentationAssetTypeSchema>;
export type PresentationCardMetadata = z.infer<typeof PresentationCardMetadataSchema>;
export type PresentationCatalog = z.infer<typeof PresentationCatalogSchema>;
export type PresentationCatalogValidationResult = ReturnType<typeof validatePresentationCatalog>;
export type StructuredCardPresentation = z.infer<typeof StructuredCardPresentationSchema>;
